// Copyright © 2019-2026 Focal Systems. All rights reserved.

/**
 * How many fields a React Hook Form `dirtyFields` tree marks as changed. Nested objects are walked
 * down to their leaves; an array counts as one field however many of its entries changed, since
 * it's edited through one control (a multi-select, a list).
 */
export function countDirtyFields(dirtyFields: unknown): number {
  if (dirtyFields === true) return 1;
  if (Array.isArray(dirtyFields)) return dirtyFields.some((entry) => countDirtyFields(entry) > 0) ? 1 : 0;
  if (dirtyFields && typeof dirtyFields === "object") {
    return Object.values(dirtyFields).reduce((sum: number, value) => sum + countDirtyFields(value), 0);
  }
  return 0;
}

/**
 * How many top-level keys of `current` differ from `baseline` — for a draft object edited outside a
 * form. A nested value (a list of actions, say) counts as one change, compared by its JSON.
 */
export function countChangedKeys(current: object, baseline: object): number {
  const a = current as Record<string, unknown>;
  const b = baseline as Record<string, unknown>;
  const keys = new Set([...Object.keys(a), ...Object.keys(b)]);
  let changes = 0;
  for (const key of keys) if (JSON.stringify(a[key]) !== JSON.stringify(b[key])) changes++;
  return changes;
}

/** How many entries were added to or removed from `baseline` — for a set of toggles edited outside a form. */
export function countSetChanges<T>(current: ReadonlySet<T>, baseline: ReadonlySet<T>): number {
  let changes = 0;
  for (const entry of current) if (!baseline.has(entry)) changes++;
  for (const entry of baseline) if (!current.has(entry)) changes++;
  return changes;
}
